// This is a port of the Telegram Android BlobDrawable class.
const MIN_SPEED = 0.8;
const MAX_SPEED = 8.2;

export type BlobDrawableState = {
  n: number;
  L: number;
  radius: Float32Array;
  angle: Float32Array;
  radiusNext: Float32Array;
  angleNext: Float32Array;
  progress: Float32Array;
  speed: Float32Array;
  minRadius: number;
  maxRadius: number;
};

const rand01 = () => Math.random();

const generateBlob = (state: BlobDrawableState, i: number, next: boolean) => {
  const angleDif = (360 / state.n) * 0.05;
  const radDif = Math.max(0, state.maxRadius - state.minRadius);
  const radius = state.minRadius + Math.abs(rand01()) * radDif;
  const angle = (360 / state.n) * i + rand01() * angleDif;
  const speed = 0.017 + 0.003 * Math.abs(rand01());

  if (next) {
    state.radiusNext[i] = radius;
    state.angleNext[i] = angle;
  } else {
    state.radius[i] = radius;
    state.angle[i] = angle;
  }
  state.speed[i] = speed;
};

export const createBlobDrawable = (
  n: number,
  minRadius: number,
  maxRadius: number,
): BlobDrawableState => {
  const state: BlobDrawableState = {
    n,
    L: (4 / 3) * Math.tan(Math.PI / (2 * n)),
    radius: new Float32Array(n),
    angle: new Float32Array(n),
    radiusNext: new Float32Array(n),
    angleNext: new Float32Array(n),
    progress: new Float32Array(n),
    speed: new Float32Array(n),
    minRadius,
    maxRadius,
  };

  for (let i = 0; i < n; i += 1) {
    generateBlob(state, i, false);
    generateBlob(state, i, true);
    state.progress[i] = 0;
  }

  return state;
};

export const setBlobRadiusRange = (
  state: BlobDrawableState,
  minRadius: number,
  maxRadius: number,
) => {
  state.minRadius = minRadius;
  state.maxRadius = maxRadius;
};

export const updateBlob = (
  state: BlobDrawableState,
  amplitude: number,
  speedScale = 1,
) => {
  for (let i = 0; i < state.n; i += 1) {
    state.progress[i] +=
      state.speed[i] * MIN_SPEED +
      amplitude * state.speed[i] * MAX_SPEED * speedScale;
    if (state.progress[i] >= 1) {
      state.progress[i] = 0;
      state.radius[i] = state.radiusNext[i];
      state.angle[i] = state.angleNext[i];
      generateBlob(state, i, true);
    }
  }
};

// Build a closed cubic-bezier path for the current blob frame.
export const buildBlobPath = (
  state: BlobDrawableState,
  cx: number,
  cy: number,
  cubicBezierK = 1,
): Path2D => {
  const path = new Path2D();
  const { n, L } = state;

  const rotate = (x: number, y: number, angleDeg: number): [number, number] => {
    const a = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const dx = x - cx;
    const dy = y - cy;
    return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
  };

  for (let i = 0; i < n; i += 1) {
    const nextIndex = i + 1 < n ? i + 1 : 0;
    const p = state.progress[i];
    const pNext = state.progress[nextIndex];

    const r1 = state.radius[i] * (1 - p) + state.radiusNext[i] * p;
    const r2 =
      state.radius[nextIndex] * (1 - pNext) +
      state.radiusNext[nextIndex] * pNext;
    const angle1 = state.angle[i] * (1 - p) + state.angleNext[i] * p;
    const angle2 =
      state.angle[nextIndex] * (1 - pNext) + state.angleNext[nextIndex] * pNext;

    const l =
      L *
      (Math.min(r1, r2) + (Math.max(r1, r2) - Math.min(r1, r2)) / 2) *
      cubicBezierK;

    const [sx, sy] = rotate(cx, cy - r1, angle1);
    const [scx, scy] = rotate(cx + l, cy - r1, angle1);
    const [ex, ey] = rotate(cx, cy - r2, angle2);
    const [ecx, ecy] = rotate(cx - l, cy - r2, angle2);

    if (i === 0) {
      path.moveTo(sx, sy);
    }
    path.bezierCurveTo(scx, scy, ecx, ecy, ex, ey);
  }

  path.closePath();
  return path;
};
